import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import UserPicker from './UserPicker'

/**
 * Start a direct message with one workspace member.
 *
 * Uniqueness is enforced via the find_existing_dm RPC — picking a user
 * you already DM with reopens the existing thread rather than creating
 * a duplicate. Group chats (N>1) live in NewGroupModal.
 *
 * Mounts with the picker focused and disabled state. The Start button
 * resolves either to an existing or a freshly-created DM, then calls
 * onDMOpen with the conversation id so the caller can navigate.
 */

export default function StartDMModal({
  onClose,
  onDMOpen,
}: {
  onClose: () => void
  onDMOpen: (conversationId: string) => void
}) {
  const { profile, workspace } = useAuth()
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const selectedId = selectedIds[0] || null

  const handleStart = async () => {
    if (!selectedId || !profile?.id || !workspace?.id) return
    setBusy(true)
    try {
      // RPC first: respects RLS, returns existing DM id or NULL.
      const { data: existingId, error: rpcErr } = await supabase
        .rpc('find_existing_dm', {
          p_workspace_id: workspace.id,
          p_user_a: profile.id,
          p_user_b: selectedId,
        })
      if (rpcErr) throw rpcErr

      if (existingId) {
        onDMOpen(existingId as string)
        return
      }

      // No existing DM — create one + insert both participants.
      const { data: created, error: cErr } = await supabase
        .from('team_conversations')
        .insert({
          workspace_id: workspace.id,
          type: 'dm',
          name: null,
          created_by: profile.id,
        })
        .select('id')
        .single()
      if (cErr) throw cErr

      const { error: pErr } = await supabase
        .from('team_conversation_participants')
        .insert([
          { team_conversation_id: created.id, user_id: profile.id },
          { team_conversation_id: created.id, user_id: selectedId },
        ])
      if (pErr) throw pErr

      queryClient.invalidateQueries({ queryKey: ['team-joined-dms-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
      onDMOpen(created.id as string)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start direct message')
      setBusy(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Start a direct message</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>Pick one teammate. If you already have a DM with them, it'll reopen.</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, padding: '16px 24px', display: 'flex', flexDirection: 'column' }}>
          {workspace?.id && profile?.id && (
            <UserPicker
              workspaceId={workspace.id}
              excludeUserIds={[profile.id]}
              mode="single"
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              placeholder="Search teammates…"
            />
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={handleStart}
            disabled={!selectedId || busy}
            className="btn-primary"
          >
            {busy ? 'Opening…' : 'Start DM'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
